/* cart-discount.js — discount code entry for the cart drawer and the cart page.

   Codes are applied with POST /cart/update.js { discount: 'CODE_A,CODE_B' } (Ajax Cart API).
   Shopify stores them server-side against the cart token and recalculates totals, so what the
   cart shows is what checkout will charge — there is no client-side state to drift.
   An empty string clears every code. Invalid codes come back 200 with
   discount_codes: [{ code, applicable: false }] rather than an HTTP error. */
class CartDiscount extends HTMLElement {
    constructor() {
        super();
        this.cartDiscountError = this.querySelector('[ref="cartDiscountError"]');
        this.cartDiscountErrorDiscountCode = this.querySelector('[ref="cartDiscountErrorDiscountCode"]');
        this.cartDiscountErrorRequest = this.querySelector('[ref="cartDiscountErrorRequest"]');
        this.cartDiscountInfo = this.querySelector('[ref="cartDiscountInfo"]');
        this.submitButton = this.querySelector('[ref="cartDiscountSubmit"]');

        this.form = this.querySelector('form');
        this.addEventListener('submit', this.submitHandler.bind(this));
        this.addEventListener('click', this.handleDynamicClick.bind(this));
        this.addEventListener('paste', this.pasteHandler.bind(this));
    }

    async submitHandler(event) {
        event.preventDefault();
        event.stopPropagation();

        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;

        const input = form.querySelector('input[name="discount"]');
        if (!(input instanceof HTMLInputElement)) return;

        await this.applyCode(input.value, input);
    }

    /* Pasting a code is unambiguous intent — nobody pastes into a discount field and then wants
       to keep editing — so submit it for them rather than making them find the checkmark.
       Typing is NOT auto-submitted: a half-typed code would fire a request per keystroke and
       flash a "cannot be applied" error at someone who is still typing. */
    pasteHandler(event) {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || input.name !== 'discount') return;

        /* The field still holds its pre-paste value during the paste event — the default action
           writes the new value afterwards. Defer a tick so we read what actually landed. */
        setTimeout(() => {
            if (!input.value.trim()) return;
            this.requestSubmit();
        });
    }

    /* Goes through the form so there is exactly one entry point to the apply flow — the busy
       guard, validation and error rendering all live behind the submit event. */
    requestSubmit() {
        if (!this.form) return;
        if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit();
        else this.form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }

    /* Applies one code and REPORTS what happened, rather than just rendering it:
       'applied' | 'duplicate' | 'shipping' | 'invalid' | 'error' | 'skipped'.
       The apply-on-checkout path at the bottom of this file has to know whether continuing to
       checkout would honour the code or silently drop it, and only the outcome can tell it. */
    async applyCode(rawCode, input) {
        if (typeof this.dataset.sectionId !== 'string') return 'skipped';

        const code = String(rawCode == null ? '' : rawCode).trim();
        if (!code) return 'skipped';
        if (this.busy) return 'skipped';

        const drawer = this.closest('cart-drawer');
        this.clearMessages();
        this.setBusy(true);

        try {
            const existing = await this.currentDiscounts();
            if (existing.some((existingCode) => this.normalize(existingCode) === this.normalize(code))) {
                if (input) input.value = '';
                return 'duplicate';
            }

            const data = await this.postDiscounts([...existing, code]);
            const returned = Array.isArray(data.discount_codes) ? data.discount_codes : [];
            const entry = returned.find((discount) => this.normalize(discount.code) === this.normalize(code));

            if (entry && entry.applicable === false) {
                if (input) input.value = '';
                this.showMessage('discount_code');
                this.updateToggleTab();
                return 'invalid';
            }

            /* Applicable, but no chip came back in the re-rendered section → it's a shipping
               discount. It IS applied; shipping just can't be priced until an address exists at
               checkout. Informational, never an error — the shopper succeeded. */
            const section = this.parseSection(data);
            if (section && entry && entry.applicable === true) {
                const rendered = Array.from(section.querySelectorAll('.cart-discount__pill'))
                    .map((element) => element.dataset.discountCode)
                    .filter(Boolean);
                if (!rendered.some((c) => this.normalize(c) === this.normalize(code))) {
                    if (input) input.value = '';
                    this.showMessage('shipping');
                    this.updateToggleTab();
                    return 'shipping';
                }
            }

            this.render(data, drawer);
            this.restoreFocus(drawer);
            return 'applied';
        } catch (error) {
            console.error('[cart-discount] Failed to apply discount:', error);
            this.showMessage('request');
            return 'error';
        } finally {
            this.setBusy(false);
        }
    }

    handleDynamicClick(event) {
        const removeButton = event.target.closest('.cart-discount__pill-remove');
        if (!removeButton) return;
        event.preventDefault();
        event.stopPropagation();
        this.removeDiscount(removeButton);
    }

    async removeDiscount(removeButton) {
        const pill = removeButton.closest('.cart-discount__pill');
        if (!pill || typeof this.dataset.sectionId !== 'string') return;

        const code = pill.dataset.discountCode;
        if (!code) return;

        /* Every write posts the WHOLE code list, so two overlapping removals both read the same
           "before" list and the second one puts the first one's code back. One at a time. */
        if (this.busy) return;

        const drawer = this.closest('cart-drawer');
        this.clearMessages();
        this.setBusy(true);

        try {
            const existing = await this.currentDiscounts();
            const remaining = existing.filter((c) => this.normalize(c) !== this.normalize(code));

            /* Posting even when nothing was filtered out is deliberate: it means the chip was
               stale (already gone server-side), and the re-render resyncs the UI instead of
               leaving a chip whose remove button appears to do nothing. */
            const data = await this.postDiscounts(remaining);
            this.render(data, drawer);
            this.restoreFocus(drawer);
        } catch (error) {
            console.error('[cart-discount] Failed to remove discount:', error);
            this.showMessage('request');
        } finally {
            this.setBusy(false);
        }
    }

    async postDiscounts(codes) {
        const config = this.fetchConfig('json', {
            body: JSON.stringify({
                discount: codes.join(','),
                sections: this.sectionsToRequest()
            })
        });
        const response = await fetch(window.routes.cart_update_url, { ...config });
        if (!response.ok) throw new Error(`cart/update.js responded ${response.status}`);
        return response.json();
    }

    /* cart-drawer.js renderContents() walks its OWN getSectionsToRender() list and dereferences
       parsedState.sections[id] for every entry. Requesting only this section leaves the rest
       undefined, and getSectionInnerHTML() then throws on a null querySelector — after the
       overlay element has already been replaced but before its click handler is re-bound,
       which silently breaks click-outside-to-close. So request everything the drawer will ask
       for, from the drawer itself, so the two lists can't drift apart. */
    sectionsToRequest() {
        const drawer = this.closest('cart-drawer');
        const ids = drawer && typeof drawer.sectionIdsToRender === 'function' ? drawer.sectionIdsToRender() : [];
        return ids.length ? ids : [this.dataset.sectionId];
    }

    render(data, drawer) {
        if (drawer) {
            drawer.renderContents(data);
            return;
        }

        /* Cart page. This component sits inside .js-contents, so the swap below destroys `this`
           — resolve everything needed up front.
           <cart-items> is the ANCESTOR of #main-cart-items, never a descendant: the original
           code searched downwards from #main-cart-items, got null, and threw before rendering
           anything, which is why the cart page never responded to Apply. */
        const footerContents = this.closest('#main-cart-footer')?.querySelector('.js-contents');
        const newContents = this.parseSection(data)?.querySelector('.js-contents');

        // Refreshes line-level discount display; it only re-fetches main-cart-items.
        const cartItems = document.querySelector('cart-items');
        if (cartItems && typeof cartItems.onCartUpdate === 'function') cartItems.onCartUpdate();

        if (footerContents && newContents) footerContents.innerHTML = newContents.innerHTML;
    }

    parseSection(data) {
        const html = data.sections && data.sections[this.dataset.sectionId];
        if (typeof html !== 'string') return null;
        return new DOMParser()
            .parseFromString(html, 'text/html')
            .getElementById(`shopify-section-${this.dataset.sectionId}`);
    }

    /* The re-render replaces the input the shopper just typed into, dropping focus to <body>.
       The drawer re-opens itself inside a setTimeout, so queue behind it. */
    restoreFocus(drawer) {
        setTimeout(() => {
            const scope = drawer
                ? document.querySelector('cart-drawer')
                : document.getElementById('main-cart-footer');
            const input = scope && scope.querySelector('cart-discount-component input[name="discount"]');
            if (input) input.focus();
        });
    }

    setBusy(isBusy) {
        this.busy = isBusy;
        if (!this.submitButton) return;
        this.submitButton.classList.toggle('is-loading', isBusy);
        this.submitButton.disabled = isBusy;
        this.submitButton.setAttribute('aria-busy', String(isBusy));
    }

    clearMessages() {
        [
            this.cartDiscountError,
            this.cartDiscountInfo,
            this.cartDiscountErrorDiscountCode,
            this.cartDiscountErrorRequest
        ].forEach((element) => element && element.classList.add('hidden'));
    }

    showMessage(type) {
        if (type === 'shipping') {
            this.cartDiscountInfo && this.cartDiscountInfo.classList.remove('hidden');
            return;
        }
        const target = type === 'request' ? this.cartDiscountErrorRequest : this.cartDiscountErrorDiscountCode;
        this.cartDiscountError && this.cartDiscountError.classList.remove('hidden');
        target && target.classList.remove('hidden');
    }

    updateToggleTab() {
        this.closest('toggle-component') && this.closest('toggle-component').resizeHandler();
    }

    /* Shopify echoes back whatever casing was submitted, but renders the discount's canonical
       title in the chip — so "save20" typed against a "SAVE20" discount must still compare equal.  */
    normalize(code) {
        return String(code).trim().toLowerCase();
    }

    /* The authority on what is actually on the cart is the cart, not the rendered chips.
       A chip only exists for a discount Shopify could price into the cart total; a free-shipping
       code applies successfully but renders NO chip (shipping isn't costed until checkout has an
       address). Deriving the list from the DOM would therefore silently DROP that code the next
       time the shopper applies another one. Falls back to the chips if the request fails. */
    async currentDiscounts() {
        try {
            const response = await fetch(`${window.routes.cart_url}.js`);
            if (!response.ok) throw new Error(`cart.js responded ${response.status}`);
            const cart = await response.json();
            if (Array.isArray(cart.discount_codes)) {
                // Rejected codes linger on the cart with applicable:false — don't resubmit them.
                return cart.discount_codes.filter((d) => d.applicable).map((d) => d.code);
            }
        } catch (error) {
            console.error('[cart-discount] Could not read cart discounts, using rendered chips:', error);
        }
        return this.existingDiscounts();
    }

    existingDiscounts() {
        const discountCodes = [];
        const discountPills = this.querySelectorAll('.cart-discount__pill');
        for (const pill of discountPills) {
            if (typeof pill.dataset.discountCode === 'string') {
                discountCodes.push(pill.dataset.discountCode);
            }
        }
        return discountCodes;
    }

    fetchConfig(type = 'json', config = {}) {
        const headers = { 'Content-Type': 'application/json', Accept: `application/${type}`, ...config.headers };
        if (type === 'javascript') {
            headers['X-Requested-With'] = 'XMLHttpRequest';
            delete headers['Content-Type'];
        }
        return { method: 'POST', headers: headers, body: config.body };
    }
}
if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}

/* Apply-on-checkout — a code typed but never submitted must not be silently discarded.

   One delegated listener on document, like assets/cart-upsell.js and assets/cart-line-edit.js:
   the drawer replaces #CartDrawer wholesale on every cart change (assets/cart-drawer.js
   renderContents), so the Check out button bound to at load time is gone after the first
   quantity change. Nothing here caches an element across the await for the same reason —
   a successful apply re-renders the drawer, detaching both the button and the form. */
(function () {
  // Outcomes where the code is on the cart, so continuing to checkout honours it.
  var PROCEED = ['applied', 'duplicate', 'shipping'];
  var submitting = false;

  /* The drawer is rendered on every page, so on /cart there are TWO discount components in the
     document. Scope to the one in the same block as the button that was clicked. */
  function componentFor(button) {
    var scope = button.closest('cart-drawer') || button.closest('#main-cart-footer');
    return scope ? scope.querySelector('cart-discount-component') : null;
  }

  /* form.submit() rather than a synthetic click: it bypasses submit listeners (nothing should
     get a second chance to intercept) and works on the freshly rendered form. It also omits the
     submit button's name/value, so `checkout` is added as a hidden field — the same shape the
     theme's own IE fallback in sections/main-cart-footer.liquid uses. */
  function submitCheckout(formId) {
    var form = formId ? document.getElementById(formId) : null;
    // Never strand the shopper on a Check out button that did nothing.
    if (!form) {
      window.location.href = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'checkout';
      return;
    }
    var flag = document.createElement('input');
    flag.type = 'hidden';
    flag.name = 'checkout';
    form.appendChild(flag);
    form.submit();
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var button = target.closest('button[name="checkout"]');
    if (!button || button.disabled) return;

    /* No component, or one the browser has not upgraded yet — there is nothing to apply, so let
       the native submit through rather than swallowing the click on a Check out button. */
    var component = componentFor(button);
    if (!component || typeof component.applyCode !== 'function') return;

    var input = component.querySelector('input[name="discount"]');
    var code = input ? input.value.trim() : '';

    // Empty field — the overwhelmingly common case. Let the native submit through untouched.
    if (!code) return;

    event.preventDefault();
    // A second click while the first apply is open would post the same code twice.
    if (submitting || component.busy) return;
    submitting = true;

    // Resolve the form id NOW: `button` does not survive the re-render an apply triggers.
    var formId = button.getAttribute('form');

    component.applyCode(code, input).then(function (outcome) {
      submitting = false;
      /* Invalid or failed → stay in the cart with the component's own error showing. They typed
         that code on purpose; dropping it and charging full price is the worse failure. */
      if (PROCEED.indexOf(outcome) === -1) return;
      submitCheckout(formId);
    });
  });
})();
